<script lang="ts">
    import { onMount } from 'svelte';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import { spinner } from '../../framework/js/spinner.js';
    import { StatusBadge } from '../../framework/components/app';
    import { invoiceStatusTone } from './adminListBadges.js';

    interface Invoice {
        uuid: string;
        invoice_number: string;
        invoice_date: string;
        due_date: string;
        total: number;
        currency?: string;
        status: string;
        user?: {
            uuid: string;
            first_name: string;
            last_name: string;
            email: string;
        };
    }

    let invoices: Invoice[] = [];
    let sortedInvoices: Invoice[] = [];
    let isLoading = false;
    let searchQuery = '';
    let statusFilter = 'all';
    let sortField = 'invoice_date';
    let sortDirection: 'asc' | 'desc' = 'desc';

    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'paid', label: 'Paid' },
        { value: 'sent', label: 'Sent' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'draft', label: 'Draft' }
    ];

    onMount(async () => {
        await loadInvoices();
    });

    async function loadInvoices() {
        isLoading = true;
        spinner.show();
        try {
            const params: Record<string, string | number> = { per_page: 500 };
            if (searchQuery.trim()) params.phrase = searchQuery.trim();
            if (statusFilter !== 'all') params.status = statusFilter;

            const response: any = await api.getAdminInvoices(params);
            if (response.success) {
                invoices = response.data || [];
                applySort();
            } else {
                toast.showNotification(false, response.message || 'Failed to load invoices');
                invoices = [];
                sortedInvoices = [];
            }
        } catch (error) {
            toast.showNotification(false, 'Error loading invoices');
            console.error(error);
            invoices = [];
            sortedInvoices = [];
        } finally {
            spinner.hide();
            isLoading = false;
        }
    }

    function applySort() {
        const result = [...invoices];
        result.sort((a, b) => {
            let aVal: string | number;
            let bVal: string | number;

            switch (sortField) {
                case 'invoice_number':
                    aVal = a.invoice_number || '';
                    bVal = b.invoice_number || '';
                    break;
                case 'user':
                    aVal = `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.toLowerCase();
                    bVal = `${b.user?.first_name || ''} ${b.user?.last_name || ''}`.toLowerCase();
                    break;
                case 'total':
                    aVal = a.total || 0;
                    bVal = b.total || 0;
                    break;
                case 'status':
                    aVal = a.status || '';
                    bVal = b.status || '';
                    break;
                default:
                    aVal = new Date(a.invoice_date).getTime();
                    bVal = new Date(b.invoice_date).getTime();
            }

            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            }
            return aVal < bVal ? 1 : -1;
        });
        sortedInvoices = result;
    }

    function handleSort(field: string) {
        if (sortField === field) {
            sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            sortField = field;
            sortDirection = 'desc';
        }
        applySort();
    }

    function formatPrice(price: number, currency = 'AUD'): string {
        try {
            return new Intl.NumberFormat(navigator.language, {
                style: 'currency',
                currency: currency.toUpperCase(),
            }).format(price);
        } catch {
            return new Intl.NumberFormat(navigator.language, {
                style: 'currency',
                currency: 'AUD',
            }).format(price);
        }
    }

    function formatDate(dateString: string): string {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString(navigator.language, {
            dateStyle: 'medium',
        });
    }

    function getTotalAmount(): number {
        return sortedInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    }

    function onStatusChange() {
        loadInvoices();
    }

    let searchDebounceTimer: ReturnType<typeof setTimeout>;
    function debounceSearch() {
        clearTimeout(searchDebounceTimer);
        searchDebounceTimer = setTimeout(() => {
            loadInvoices();
        }, 300);
    }

    $: displayCount = sortedInvoices.length;
</script>

<div class="page-shell admin-list">
    <div class="page-header">
        <div class="header-content">
            <h2 class="page-title">All Invoices</h2>
            <p class="page-subtitle">View and manage all invoices across all users</p>
        </div>
        <div class="header-stats">
            <span class="stat-badge">{displayCount} invoice{displayCount !== 1 ? 's' : ''}</span>
            <span class="stat-badge stat-badge--success">{formatPrice(getTotalAmount())}</span>
        </div>
    </div>

    <div class="filters-bar">
        <div class="filters-group">
            <div class="search-box">
                <input
                    type="text"
                    placeholder="Search by invoice number, user name, or email..."
                    bind:value={searchQuery}
                    on:input={debounceSearch}
                    class="search-input"
                    aria-label="Search invoices"
                />
            </div>
            <div class="filter-select">
                <label for="admin-invoice-status-filter" class="sr-only">Filter by status</label>
                <select id="admin-invoice-status-filter" bind:value={statusFilter} on:change={onStatusChange}>
                    {#each statusOptions as option}
                        <option value={option.value}>{option.label}</option>
                    {/each}
                </select>
            </div>
        </div>
    </div>

    {#if isLoading}
        <div class="loading-state">Loading invoices...</div>
    {:else if sortedInvoices.length === 0}
        <div class="empty-state">
            <i class="file alternate outline icon empty-icon" aria-hidden="true"></i>
            <p>No invoices found.</p>
            {#if searchQuery || statusFilter !== 'all'}
                <button type="button" class="clear-filters-btn" on:click={() => { searchQuery = ''; statusFilter = 'all'; loadInvoices(); }}>
                    Clear filters
                </button>
            {/if}
        </div>
    {:else}
        <div class="table-wrap invoices-table-wrap">
            <table class="data-table invoices-table">
                <thead>
                    <tr>
                        <th class="sortable col-invoice" on:click={() => handleSort('invoice_number')}>
                            Invoice #
                            {#if sortField === 'invoice_number'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable col-user" on:click={() => handleSort('user')}>
                            User
                            {#if sortField === 'user'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable col-date" on:click={() => handleSort('invoice_date')}>
                            Date
                            {#if sortField === 'invoice_date'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="col-date">Due</th>
                        <th class="sortable col-money" on:click={() => handleSort('total')}>
                            Amount
                            {#if sortField === 'total'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                        <th class="sortable col-status" on:click={() => handleSort('status')}>
                            Status
                            {#if sortField === 'status'}
                                <i class="icon {sortDirection === 'asc' ? 'arrow up' : 'arrow down'}" aria-hidden="true"></i>
                            {/if}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {#each sortedInvoices as invoice (invoice.uuid)}
                        <tr class="data-row">
                            <td class="col-invoice">
                                <span class="invoice-number">{invoice.invoice_number}</span>
                            </td>
                            <td class="user-cell col-user">
                                {#if invoice.user}
                                    <div class="user-name">
                                        {invoice.user.first_name || ''} {invoice.user.last_name || ''}
                                    </div>
                                    <div class="user-email">{invoice.user.email}</div>
                                {:else}
                                    <span class="text-muted">Unknown User</span>
                                {/if}
                            </td>
                            <td class="date-cell col-date">{formatDate(invoice.invoice_date)}</td>
                            <td class="date-cell col-date">{formatDate(invoice.due_date)}</td>
                            <td class="col-money">{formatPrice(invoice.total, invoice.currency)}</td>
                            <td class="col-status">
                                <StatusBadge
                                    tone={invoiceStatusTone(invoice.status)}
                                    label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                />
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}
</div>

<style>
    .invoice-number {
        font-weight: 500;
        color: var(--grey);
        font-variant-numeric: tabular-nums;
    }

    .invoices-table-wrap :global(.invoices-table) {
        table-layout: fixed;
        width: 100%;
    }

    .invoices-table-wrap :global(.invoices-table thead th),
    .invoices-table-wrap :global(.invoices-table tbody td) {
        vertical-align: top;
    }

    .invoices-table-wrap :global(.invoices-table .col-invoice) {
        width: 14%;
        text-align: left;
    }

    .invoices-table-wrap :global(.invoices-table .col-user) {
        width: 28%;
        text-align: left;
    }

    .invoices-table-wrap :global(.invoices-table .col-date) {
        width: 12%;
        text-align: left;
        white-space: nowrap;
    }

    .invoices-table-wrap :global(.invoices-table .col-money) {
        width: 12%;
        text-align: right;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    .invoices-table-wrap :global(.invoices-table .col-status) {
        width: 12%;
        text-align: left;
    }

    .invoices-table-wrap :global(.invoices-table thead th.col-money) {
        text-align: right;
    }

    @media (max-width: 1100px) {
        .invoices-table-wrap :global(.invoices-table) {
            table-layout: auto;
            min-width: 720px;
        }
    }
</style>
